'use client';

import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { useEffect, useState } from "react";
import { NewsItem } from "@/lib/api";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const ITEMS_PER_PAGE = 4;

export default function NewsArchive() {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://ymfierp.yegnamicrofinance.com/api/news/featured?page=${currentPage}&per_page=${ITEMS_PER_PAGE}`
        );
        const data = await response.json();

        const withFormattedDate = (data.data || []).map((item: NewsItem) => ({
          ...item,
          formatted_date: item.date
            ? new Date(item.date).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
            : item.date || "Unknown",
        }));

        setNewsItems(withFormattedDate);
        setTotalPages(data.meta?.last_page || 1);
      } catch (error) {
        console.error('Error fetching news:', error);
        setError('Failed to load news');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [currentPage]);

  const handleReadMore = (news: NewsItem) => {
    setSelectedNews(news);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setSelectedNews(null);
    document.body.style.overflow = 'auto';
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-3 border-t-transparent mx-auto" style={{ borderColor: `${colors.primary} transparent ${colors.primary} transparent` }}></div>
          <p className="mt-3 text-sm font-medium" style={{ color: colors.primary }}>Loading news...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 px-4" style={{ color: "#DC2626" }}>
        <div className="text-5xl mb-3">😅</div>
        <p className="text-lg font-medium mb-3">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-5 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90 hover:scale-105 text-sm"
          style={{ backgroundColor: colors.primary }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <section className="py-8 md:py-12 lg:py-16" style={{ background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.primary}05 50%, ${colors.white} 100%)` }}>
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header - Smaller */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8 text-center md:mb-12 lg:mb-14 pt-6 md:pt-8"
        >
          <motion.div 
            className="mb-3 inline-flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <span className="h-0.5 w-8 rounded-full" style={{ backgroundColor: colors.primary }}></span>
            <span className="px-3 text-xs font-semibold uppercase tracking-wider" style={{ color: colors.primary }}>
              Latest Updates
            </span>
            <span className="h-0.5 w-8 rounded-full" style={{ backgroundColor: colors.primary }}></span>
          </motion.div>
          <h1 className="mb-2 text-2xl font-bold md:text-3xl lg:text-4xl" style={{ color: colors.textDark }}>
            News & <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primaryDark}, ${colors.primary}, ${colors.primaryLight})` }}>Announcements</span>
          </h1>
          <p className="mx-auto max-w-2xl text-sm md:text-base lg:text-lg leading-relaxed" style={{ color: colors.textDark }}>
            Stay updated with the latest developments, financial insights, and company news.
          </p>
        </motion.div>

        {/* News Cards Grid - Smaller Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-5">
          <AnimatePresence mode="wait">
            {newsItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                viewport={{ once: true, margin: "-30px" }}
                className="group"
              >
                <article 
                  className="flex h-full flex-col overflow-hidden rounded-xl border shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-1" 
                  style={{ borderColor: `${colors.primary}15`, backgroundColor: colors.white }}
                >
                  {/* Image Container - Smaller */}
                  <div className="relative h-40 w-full overflow-hidden sm:h-44 md:h-36 lg:h-40" style={{ backgroundColor: `${colors.primary}10` }}>
                    {item.image ? (
                      <>
                        <Image
                          src={item.image}
                          alt={item.title}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-105"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = document.createElement('div');
                              fallback.className = 'w-full h-full flex items-center justify-center';
                              fallback.style.backgroundColor = `${colors.primary}10`;
                              fallback.innerHTML = `<span style="color:${colors.primary};font-size:2.5rem;font-weight:bold;">📰</span>`;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl" style={{ color: colors.primary }}>📰</span>
                      </div>
                    )}
                    
                    {/* Category Badge - Smaller */}
                    <div 
                      className="absolute top-2 left-2 rounded-full px-2.5 py-0.5 text-[10px] font-semibold text-white shadow-md backdrop-blur-sm transition-all group-hover:scale-105"
                      style={{ backgroundColor: `${colors.primary}90` }}
                    >
                      {item.category || "General"}
                    </div>

                    {/* Date Badge - Smaller */}
                    <div 
                      className="absolute bottom-2 right-2 rounded-lg px-2.5 py-0.5 text-[10px] font-medium text-white shadow-md backdrop-blur-sm"
                      style={{ backgroundColor: `rgba(0,0,0,0.6)` }}
                    >
                      {item.date}
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-3.5 sm:p-4 md:p-4.5">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: colors.primary }}>
                        {item.category || "General"}
                      </span>
                    </div>

                    <h3 className="mb-2 line-clamp-2 text-sm font-bold leading-snug md:text-base transition-colors group-hover:text-primary" style={{ color: colors.textDark }}>
                      {item.title}
                    </h3>
                    
                    <p className="mb-3 line-clamp-2 flex-1 text-xs md:text-sm leading-relaxed" style={{ color: colors.textDark }}>
                      {item.excerpt}
                    </p>

                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.primary}15` }}>
                          <span className="text-[10px]" style={{ color: colors.primary }}>📬</span>
                        </div>
                        <span className="text-[10px] opacity-60" style={{ color: colors.textDark }}>Read more</span>
                      </div>
                      <button
                        onClick={() => handleReadMore(item)}
                        className="inline-flex items-center gap-1 font-medium transition-all duration-300 hover:gap-1.5 group-hover:translate-x-0.5 text-xs"
                        style={{ color: colors.primary }}
                      >
                        <span>Read More</span>
                        <svg
                          className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </article>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Pagination Controls - Smaller */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mt-8 flex justify-center"
          >
            <div className="flex items-center gap-1.5 flex-wrap justify-center">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3.5 py-2 rounded-lg font-medium transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 text-sm"
                style={{ 
                  backgroundColor: currentPage === 1 ? `${colors.primary}10` : `${colors.primary}15`,
                  color: currentPage === 1 ? colors.textDark : colors.primary
                }}
              >
                <span className="flex items-center gap-1">
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </span>
              </button>
              
              <div className="flex items-center gap-1 mx-1.5">
                {Array.from({ length: totalPages }, (_, i) => {
                  const pageNum = i + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    Math.abs(pageNum - currentPage) <= 1
                  ) {
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNum)}
                        className={`min-w-[32px] h-[32px] rounded-lg font-medium transition-all duration-300 hover:scale-105 text-sm ${
                          currentPage === pageNum
                            ? "text-white shadow-md"
                            : ""
                        }`}
                        style={{
                          backgroundColor: currentPage === pageNum ? colors.primary : `${colors.primary}10`,
                          color: currentPage === pageNum ? colors.white : colors.textDark
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  if (pageNum === 2 && currentPage > 3) {
                    return <span key="ellipsis1" className="px-1 text-sm" style={{ color: colors.textDark }}>...</span>;
                  }
                  if (pageNum === totalPages - 1 && currentPage < totalPages - 2) {
                    return <span key="ellipsis2" className="px-1 text-sm" style={{ color: colors.textDark }}>...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3.5 py-2 rounded-lg font-medium transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 text-sm"
                style={{ 
                  backgroundColor: currentPage === totalPages ? `${colors.primary}10` : `${colors.primary}15`,
                  color: currentPage === totalPages ? colors.textDark : colors.primary
                }}
              >
                <span className="flex items-center gap-1">
                  Next
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
            </div>
          </motion.div>
        )}

        {/* Modal - Smaller */}
        <AnimatePresence>
          {selectedNews && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 flex items-center justify-center backdrop-blur-md z-50 p-4 overflow-y-auto"
              onClick={handleCloseModal}
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-xl max-w-2xl w-full p-4 sm:p-5 md:p-6 max-h-[90vh] overflow-y-auto shadow-2xl"
                style={{ backgroundColor: colors.white }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={handleCloseModal}
                  className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center transition-all hover:rotate-90 hover:scale-110 text-sm"
                  style={{ backgroundColor: `${colors.primary}10`, color: colors.textDark }}
                >
                  ✕
                </button>

                <div className="mb-3">
                  <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: colors.primary }}>
                    {selectedNews.category || "General"}
                  </span>
                  <h2 className="text-lg font-bold mt-1 sm:text-xl lg:text-2xl" style={{ color: colors.textDark }}>
                    {selectedNews.title}
                  </h2>
                  <span className="text-xs mt-1.5 block" style={{ color: colors.primary }}>
                    {selectedNews.date}
                  </span>
                </div>

                <div className="relative h-40 w-full overflow-hidden rounded-lg sm:h-48 md:h-52" style={{ backgroundColor: `${colors.primary}10` }}>
                  {selectedNews.image ? (
                    <Image
                      src={selectedNews.image}
                      alt={selectedNews.title}
                      fill
                      className="object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-4xl" style={{ color: colors.primary }}>📰</span>
                    </div>
                  )}
                </div>

                <div
                  className="prose prose-sm max-h-[35vh] overflow-y-auto mt-3 prose-headings:text-textDark prose-p:text-textDark prose-strong:text-textDark"
                  style={{ color: colors.textDark }}
                  dangerouslySetInnerHTML={{ __html: selectedNews.content }}
                />
                
                <button
                  onClick={handleCloseModal}
                  className="mt-4 text-white px-5 py-2 rounded-lg font-medium transition-all duration-300 hover:opacity-90 hover:scale-105 shadow-md w-full sm:w-auto text-sm"
                  style={{ backgroundColor: colors.primary }}
                >
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}